/**
 * Per-campaign Turnstile mode (testable without DOM / Cloudflare).
 * checkout-gateway POST /session returns turnstile: "on" | "watch" | "off".
 * The server mode wins; when absent (older gateway) fall back to local turnstileEnabled.
 * The mode is stored with the gateway session only and cleared with it, so a
 * server-side change applies on the visitor's next checkout.
 */

const MODES = ['on', 'watch', 'off'];
const MODE_STORAGE_KEY = 'checkoutGatewayTurnstileMode';
const WATCH_TIMEOUT_MS = 5000;

/**
 * @returns {'on'|'watch'|'off'|null}
 */
function normalizeMode(value) {
	if (typeof value !== 'string') return null;
	const mode = value.trim().toLowerCase();
	return MODES.indexOf(mode) === -1 ? null : mode;
}

/**
 * @param {*} serverMode value from the session response (may be absent)
 * @param {boolean} localEnabled site config turnstileEnabled (flag only; the
 *   site key is checked in getTurnstileToken — see turnstile.js)
 * @returns {'on'|'watch'|'off'}
 */
function resolveMode(serverMode, localEnabled) {
	return normalizeMode(serverMode) || (localEnabled ? 'on' : 'off');
}

/**
 * Obtain a token according to the mode.
 * - on: token required; failures reject (existing shopper-facing error).
 *   getToken resolves '' only when the ts-bypass is active.
 * - watch: best effort with a short timeout; any failure resolves '' so the
 *   order is still sent. Never blocks the shopper for Turnstile reasons.
 * - off: never loads Turnstile; resolves ''.
 * @param {string} mode
 * @param {() => Promise<string>} getToken
 * @param {{ timeoutMs?: number, debug?: Function }} [opts]
 * @returns {Promise<string>}
 */
function acquireToken(mode, getToken, opts = {}) {
	if (mode === 'off') {
		return Promise.resolve('');
	}
	if (mode !== 'watch') {
		return Promise.resolve().then(getToken);
	}

	const timeoutMs = opts.timeoutMs || WATCH_TIMEOUT_MS;
	const debug = opts.debug || (() => {});
	let timer = null;
	const timeout = new Promise((resolve) => {
		timer = setTimeout(() => {
			debug(`Turnstile watch mode: no token after ${timeoutMs}ms, sending order without token`);
			resolve('');
		}, timeoutMs);
	});
	const attempt = Promise.resolve()
		.then(getToken)
		.catch((err) => {
			debug('Turnstile watch mode: ' + (err && err.message) + ', sending order without token');
			return '';
		});
	return Promise.race([attempt, timeout]).then((token) => {
		clearTimeout(timer);
		return token || '';
	});
}

function saveSessionMode(storage, serverMode) {
	const mode = normalizeMode(serverMode);
	try {
		if (mode) {
			storage.setItem(MODE_STORAGE_KEY, mode);
		} else {
			storage.removeItem(MODE_STORAGE_KEY);
		}
	} catch (e) {
		/* ignore quota / private mode — fallback to local config applies */
	}
}

/**
 * @returns {'on'|'watch'|'off'|null} null when the gateway did not send a mode
 */
function readSessionMode(storage) {
	try {
		return normalizeMode(storage.getItem(MODE_STORAGE_KEY));
	} catch (e) {
		return null;
	}
}

function clearSessionMode(storage) {
	try {
		storage.removeItem(MODE_STORAGE_KEY);
	} catch (e) {
		/* ignore */
	}
}

export {
	MODE_STORAGE_KEY,
	WATCH_TIMEOUT_MS,
	normalizeMode,
	resolveMode,
	acquireToken,
	saveSessionMode,
	readSessionMode,
	clearSessionMode,
};
